import React, { useEffect, useState, useRef } from "react";
import "../Styling/customerLedger.css";
import { Base_URL } from "../url";
import { useReactToPrint } from "react-to-print";
import { fetchCompnayCodesById, fetchCustomersByOrgId } from "../Api/InvoiceApi";
import { useAuth } from "../Context/AuthContext";

export default function CustomerLedger() {
  const { user } = useAuth();

  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedCompanyCode, setSelectedCompanyCode] = useState("");

  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomerCode, setSelectedCustomerCode] = useState("");

  const [ledger, setLedger] = useState({
    customer: {
      customerCode: "",
      name: "",
      address1: "",
      address2: "",
      address3: "",
      cityName: "",
      pinCode: "",
      mobileCountrycode: "",
      mobileNumber: "",
      gstno: "",
      pan: "",
      organisationId: "",
      companyCode: "",
      active: "",
    },
    openingBalance: 0,
    currency: "INR",
    transactions: [],
  });

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: "Customer Ledger",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load dropdowns
  useEffect(() => {
    async function loadCompanies() {
      try {
        if (!user?.organisationId) return;
        const opts = await fetchCompnayCodesById(user.organisationId);
        setCompanyOptions(opts || []);
      } catch (e) {
        console.error("Failed to load company options", e);
      }
    }

    async function loadCustomers() {
      try {
        if (!user?.organisationId) return;
        const custs = await fetchCustomersByOrgId(user.organisationId);
        setCustomerOptions(custs || []);
      } catch (e) {
        console.error("Failed to load customers", e);
      }
    }

    loadCompanies();
    loadCustomers();
  }, [user]);

  // LEDGER MAPPING: customer entries = DEBIT, advance = CREDIT
 // LEDGER MAPPING: customer entries = DEBIT, advance = CREDIT
const fetchLedger = async () => {
  setLoading(true);
  setError(null);

  try {
    if (!selectedCustomerCode) {
      throw new Error("Please select a customer.");
    }

    const companyQuery = selectedCompanyCode
      ? `&companyCode=${encodeURIComponent(selectedCompanyCode)}`
      : "";
    const url = `${Base_URL}/AdvancePayment/GetLedgerData?customerCode=${encodeURIComponent(
      selectedCustomerCode
    )}${companyQuery}`;

    const res = await fetch(url);
    if (!res.ok) throw new Error(`Server responded ${res.status}`);
    const data = await res.json();

    const customer = data.customer || {};
    const acitsCustomer = data.acitsCustomer || []; // DEBIT entries (invoices)
    const acitAdvance = data.acitAdvance || [];     // CREDIT entries (advances)
    const openingBalance = data.openingBalance ?? 0;

    // Merge & sort by date so ledger is strictly chronological
    const rows = [
      ...acitsCustomer.map((r) => ({ ...r, __kind: "customer" })),
      ...acitAdvance.map((r) => ({ ...r, __kind: "advance" })),
    ].sort(
      (a, b) =>
        new Date(a.postingDate || a.createdDate || 0) -
        new Date(b.postingDate || b.createdDate || 0)
    );

    let running = openingBalance;
    const transactions = [];

    rows.forEach((row) => {
      const amount = row.amount || 0;
      const isCustomer = row.__kind === "customer";

      const date = row.postingDate || row.createdDate || null;
      const ref = row.docno || row.salesInvoiceNo || "";
      const type = row.transactionCode || row.postingkey || "";
      const description = row.reportingWbs || row.salesInvoiceNo || "";

      let debit = null;
      let credit = null;
      let documentName = "";

      if (isCustomer) {
        // CUSTOMER ENTRY = DEBIT (invoice raised to customer)
        debit = amount;
        running += amount;
        documentName = "Invoice";
      } else {
        // ADVANCE ENTRY = CREDIT (customer paid us)
        credit = amount;
        running -= amount;
        documentName = "Advance";
      }

      transactions.push({
        date,
        ref,
        type,
        description,
        debit,
        credit,
        balance: running,
        documentName,
      });
    });

    setLedger({
      customer: {
        customerCode: customer.customerCode || "",
        name: customer.name || "",
        address1: customer.address1 || "",
        address2: customer.address2 || "",
        address3: customer.address3 || "",
        cityName: customer.cityName || "",
        pinCode: customer.pinCode || customer.pincode || "",
        mobileCountrycode: customer.mobileCountrycode || "",
        mobileNumber: customer.mobileNumber || "",
        gstno: customer.gstno || "",
        pan: customer.pan || "",
        organisationId: customer.organisationId || "",
        companyCode: customer.companyCode || "",
        active: customer.active || "",
      },
      openingBalance,
      currency: "INR",
      transactions,
    });
  } catch (err) {
    console.error(err);
    setError(err.message || "Failed to load ledger");
  } finally {
    setLoading(false);
  }
};


  const handleSubmit = (e) => {
    e.preventDefault();
    fetchLedger();
  };

  function Money({ value }) {
    const formatted = new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR", // fixed to INR
      maximumFractionDigits: 2,
    }).format(value || 0);
    return <span>{formatted}</span>;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Filters */}
        <form
          onSubmit={handleSubmit}
          className="flex items-center justify-between mb-6"
        >
          <div>
            <h1 className="text-2xl font-semibold">Customer Ledger</h1>
            <p className="text-sm text-gray-600">
              {selectedCustomerCode || "No customer selected"}
            </p>
          </div>

          <div className="flex gap-3 items-center">
            {/* Customer dropdown */}
            <div>
              <label className="text-sm text-gray-600 mr-2">Customer:</label>
              <select
                value={selectedCustomerCode}
                onChange={(e) => setSelectedCustomerCode(e.target.value)}
                className="px-3 py-2 border rounded"
              >
                <option value="">-- Select Customer --</option>
                {customerOptions.map((c, i) => (
                  <option
                    key={i}
                    value={c.customerCode || c.code || c.value}
                  >
                    {c.name ||
                      c.shortNames ||
                      c.label ||
                      c.customerCode ||
                      c.code}
                  </option>
                ))}
              </select>
            </div>

            {/* Company dropdown */}
            <div>
              <label className="text-sm text-gray-600 mr-2">Company:</label>
              <select
                value={selectedCompanyCode}
                onChange={(e) => setSelectedCompanyCode(e.target.value)}
                className="px-3 py-2 border rounded"
              >
                <option value="">-- Select Company --</option>
                {companyOptions.map((c, i) => (
                  <option
                    key={i}
                    value={c.companyCode || c.code || c.value}
                  >
                    {c.companyName ||
                      c.description ||
                      c.label ||
                      c.companyCode ||
                      c.code}
                  </option>
                ))}
              </select>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                type="submit"
                className="px-4 py-2 bg-green-600 text-white rounded shadow hover:bg-green-700"
                disabled={loading}
              >
                {loading ? "Loading..." : "Get Ledger"}
              </button>

              <button
                type="button"
                onClick={handlePrint}
                className="px-4 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700"
              >
                Download PDF
              </button>

              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-white border rounded shadow"
              >
                Refresh
              </button>
            </div>
          </div>
        </form>

        {/* Ledger content */}
        <div ref={componentRef} className="bg-white rounded-lg shadow p-6 border2">
          {/* Customer header */}
          <div className="flex justify-between items-start border-b pb-4 mb-4 border2 p-4 rounded-lg">
            <div>
              <h2 className="text-lg font-medium">{ledger.customer.name}</h2>
              <p className="text-sm text-gray-600">
                Code: {ledger.customer.customerCode}
              </p>
              <p className="text-sm text-gray-600">
                {ledger.customer.address1} {ledger.customer.address2}{" "}
                {ledger.customer.address3}
              </p>
              <p className="text-sm text-gray-600">
                {ledger.customer.cityName} - {ledger.customer.pinCode}
              </p>
              <p className="text-sm text-gray-600">
                GST: {ledger.customer.gstno || "-"} | PAN:{" "}
                {ledger.customer.pan || "-"}
              </p>
              <p className="text-sm text-gray-600">
                Mob: {ledger.customer.mobileNumber || "-"}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">
                As on: {new Date().toLocaleDateString()}
              </p>
              <p className="text-xl font-semibold mt-2">Closing Balance</p>
              <p className="text-2xl font-bold">
                <Money
                  value={
                    ledger.transactions.length
                      ? ledger.transactions[ledger.transactions.length - 1]
                          .balance
                      : ledger.openingBalance
                  }
                />
              </p>
            </div>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="p-3 border rounded">
              <p className="text-xl text-gray-500">Opening Balance</p>
              <p className="text-lg mt-1">
                <Money value={ledger.openingBalance} />
              </p>
            </div>
            <div className="p-3 border rounded">
              <p className="text-xl text-gray-500">Total Debits</p>
              <p className="text-lg mt-1">
                <Money
                  value={ledger.transactions.reduce(
                    (s, t) => s + (t.debit || 0),
                    0
                  )}
                />
              </p>
            </div>
            <div className="p-3 border rounded">
              <p className="text-xl text-gray-500">Total Credits</p>
              <p className="text-lg mt-1">
                <Money
                  value={ledger.transactions.reduce(
                    (s, t) => s + (t.credit || 0),
                    0
                  )}
                />
              </p>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-auto border-collapse">
              <thead>
                <tr className="text-left bg-gray-100">
                  <th className="p-2 border">Date</th>
                  <th className="p-2 border">Document Number</th>
                  <th className="p-2 border">Type</th>
                  <th className="p-2 border">Reference Number</th>
                  <th className="p-2 border text-right">Debit</th>
                  <th className="p-2 border text-right">Credit</th>
                  <th className="p-2 border text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td
                      colSpan={7}
                      className="p-4 text-center text-gray-500"
                    >
                      Loading...
                    </td>
                  </tr>
                )}

                {!loading && ledger.transactions.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="p-4 text-center text-gray-500"
                    >
                      No transactions found.
                    </td>
                  </tr>
                )}

                {ledger.transactions.map((tx, idx) => (
                  <tr
                    key={idx}
                    className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="p-2 border align-top">
                      {tx.date ? new Date(tx.date).toLocaleDateString() : ""}
                    </td>
                    <td className="p-2 border align-top">{tx.ref}</td>
                    <td className="p-2 border align-top">{tx.documentName}</td>
                    <td className="p-2 border align-top">{tx.description}</td>
                    <td className="p-2 border text-right align-top">
                      {tx.debit != null ? <Money value={tx.debit} /> : ""}
                    </td>
                    <td className="p-2 border text-right align-top">
                      {tx.credit != null ? <Money value={tx.credit} /> : ""}
                    </td>
                    <td className="p-2 border text-right align-top">
                      <Money value={tx.balance} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {error && <p className="mt-4 text-red-600">Error: {error}</p>}
        </div>
      </div>
    </div>
  );
}
